import React, { useEffect, useRef, useState } from 'react';
import { MoveDiagonal2, MoveHorizontal, MoveVertical, RotateCcw } from 'lucide-react';

const STORAGE_KEY = 'cnr:gl_ledger_dimensions';
const MIN_WIDTH = 640;
const MIN_HEIGHT = 400;
const MAX_HEIGHT = 1200;

const loadDimensions = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return { width: Number(saved.width) || null, height: Number(saved.height) || 720 };
  } catch { return { width: null, height: 720 }; }
};
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const ResizableLedgerWorkspace = ({ children }) => {
  const workspaceRef = useRef(null);
  const dragRef = useRef(null);
  const [dimensions, setDimensions] = useState(loadDimensions);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dimensions));
  }, [dimensions]);

  useEffect(() => {
    const node = workspaceRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => setMeasuredWidth(Math.round(node.getBoundingClientRect().width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const applySize = (width, height) => {
    const parentWidth = workspaceRef.current?.parentElement?.clientWidth || width;
    setDimensions({
      width: clamp(width, Math.min(MIN_WIDTH, parentWidth), parentWidth),
      height: clamp(height, MIN_HEIGHT, MAX_HEIGHT),
    });
  };

  const startDrag = (axis, event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const rect = workspaceRef.current.getBoundingClientRect();
    dragRef.current = { axis, x: event.clientX, y: event.clientY, width: rect.width, height: rect.height };
    setDragging(true);
    document.body.style.cursor = axis === 'x' ? 'col-resize' : axis === 'y' ? 'row-resize' : 'nwse-resize';
    document.body.style.userSelect = 'none';
    const move = (nextEvent) => {
      const start = dragRef.current;
      if (!start) return;
      applySize(
        start.axis === 'y' ? start.width : start.width + nextEvent.clientX - start.x,
        start.axis === 'x' ? start.height : start.height + nextEvent.clientY - start.y,
      );
    };
    const stop = () => {
      dragRef.current = null; setDragging(false);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      window.removeEventListener('pointermove', move, true); window.removeEventListener('pointerup', stop, true);
      window.removeEventListener('mousemove', move, true); window.removeEventListener('mouseup', stop, true);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', stop, true);
    window.addEventListener('mousemove', move, true);
    window.addEventListener('mouseup', stop, true);
  };

  const keyResize = (axis, event) => {
    const amount = event.shiftKey ? 50 : 10;
    const rect = workspaceRef.current.getBoundingClientRect();
    let width = rect.width; let height = rect.height;
    if ((axis === 'x' || axis === 'both') && ['ArrowLeft', 'ArrowRight'].includes(event.key)) width += event.key === 'ArrowRight' ? amount : -amount;
    else if ((axis === 'y' || axis === 'both') && ['ArrowUp', 'ArrowDown'].includes(event.key)) height += event.key === 'ArrowDown' ? amount : -amount;
    else return;
    event.preventDefault(); applySize(width, height);
  };

  const handleProps = (axis, testId, label) => ({
    role: 'separator', tabIndex: 0, 'aria-label': label,
    'aria-valuemin': axis === 'y' ? MIN_HEIGHT : Math.min(MIN_WIDTH, measuredWidth || MIN_WIDTH),
    'aria-valuemax': axis === 'y' ? MAX_HEIGHT : workspaceRef.current?.parentElement?.clientWidth || measuredWidth,
    'aria-valuenow': axis === 'y' ? dimensions.height : measuredWidth,
    'data-testid': testId,
    onPointerDown: (event) => startDrag(axis, event),
    onKeyDown: (event) => keyResize(axis === 'corner' ? 'both' : axis, event),
  });

  return (
    <div className="mt-4 min-w-0" data-testid="ledger-resize-stage">
      <div className="mb-2 flex flex-wrap items-center justify-end gap-2 text-[10.5px] text-muted-foreground">
        <span data-testid="ledger-dimensions-label">Workspace · {measuredWidth || '—'} × {dimensions.height}px</span>
        <button type="button" onClick={() => setDimensions({ width: null, height: 720 })} className="inline-flex h-7 items-center gap-1 rounded-sm border border-border bg-background px-2 hover:border-primary" data-testid="ledger-reset-dimensions-btn"><RotateCcw className="h-3 w-3" /> Reset size</button>
      </div>
      <div
        ref={workspaceRef}
        className={`relative overflow-auto rounded-md border border-border bg-background shadow-sm transition-[box-shadow] ${dragging ? 'shadow-lg ring-1 ring-primary/40' : ''}`}
        style={{ width: dimensions.width ? `min(${dimensions.width}px, 100%)` : '100%', height: dimensions.height, maxWidth: '100%' }}
        data-testid="ledger-workspace-resizable"
      >
        <div className="min-w-[640px] p-3">{children}</div>
        <div {...handleProps('x', 'ledger-resize-handle-x', 'Resize ledger width')} className="absolute inset-y-10 right-0 z-10 flex w-2 cursor-col-resize items-center justify-center bg-primary/0 hover:bg-primary/10 focus:bg-primary/10 focus:outline-none"><MoveHorizontal className="h-3 w-3 text-primary opacity-0 hover:opacity-100" /></div>
        <div {...handleProps('y', 'ledger-resize-handle-y', 'Resize ledger height')} className="absolute inset-x-10 bottom-0 z-10 flex h-2 cursor-row-resize items-center justify-center bg-primary/0 hover:bg-primary/10 focus:bg-primary/10 focus:outline-none"><MoveVertical className="h-3 w-3 text-primary opacity-0 hover:opacity-100" /></div>
        <div {...handleProps('corner', 'ledger-resize-handle-corner', 'Resize ledger width and height')} className="absolute bottom-0 right-0 z-20 grid h-7 w-7 cursor-nwse-resize place-items-center rounded-tl-md border-l border-t border-border bg-card text-primary hover:bg-primary/10 focus:bg-primary/10 focus:outline-none"><MoveDiagonal2 className="h-4 w-4" /></div>
      </div>
    </div>
  );
};